import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider } from "react-hook-form";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Text } from "@medoma/components/Text/Text";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { DialogPanel } from "@medoma/components/DialogPanel/DialogPanel";
import {
  closeReason,
  type IActivity,
  patientActivitiesQueryOptions,
  reactivateClosedActivities,
} from "@/api/Activities";
import { ActivitiesToCloseOrReactivate } from "@/pages/commandcenter/Patients/Patient/CareEpisode/ActivitiesToCloseOrReactivate";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import NoResults from "@/components/NoResults/NoResults";
import styles from "./ReactivateActivitiesDialog.module.scss";

type ReactivateActivitiesFormData = {
  activityIds: string[];
};

export const ReactivateActivitiesDialog = ({
  isOpen,
  onClose,
  oldCareEpisodeId,
  patientId,
}: {
  isOpen: boolean;
  onClose: () => void;
  oldCareEpisodeId: string;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    data: closedActivities,
    isPending,
    isError,
  } = useQuery(
    patientActivitiesQueryOptions({
      patientId,
      closeReason: closeReason.enum["discharge-or-decline"],
      oldCareEpisodeId,
    }),
  );

  if (isPending) {
    return (
      <DialogPanel
        isOpen={isOpen}
        onClose={onClose}
        title={t`Återaktivera aktiviteter`}
      >
        <Loading message={t`Hämtar patientens tidigare aktiviteter`} />
      </DialogPanel>
    );
  }

  return (
    <DialogPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t`Återaktivera aktiviteter`}
    >
      <ReactivateActivitiesForm
        patientId={patientId}
        oldCareEpisodeId={oldCareEpisodeId}
        closedActivities={closedActivities ?? []}
        errorFetchingActivities={isError}
        onComplete={onClose}
      />
    </DialogPanel>
  );
};

const ReactivateActivitiesForm = ({
  patientId,
  oldCareEpisodeId,
  closedActivities,
  errorFetchingActivities,
  onComplete,
}: {
  patientId: string;
  oldCareEpisodeId: string;
  closedActivities: IActivity[];
  errorFetchingActivities: boolean;
  onComplete: () => void;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const methods = useForm<ReactivateActivitiesFormData>({
    defaultValues: {
      activityIds: closedActivities.map(({ id }) => id),
    },
  });

  const { handleSubmit, formState } = methods;

  const { mutate: reactivateActivitiesMutation, isPending: isReactivating } =
    useMutation({
      mutationFn: async (activityIds: string[]) => {
        if (activityIds.length > 0) {
          await reactivateClosedActivities({
            patientId,
            closeReason: closeReason.enum["discharge-or-decline"],
            oldCareEpisodeId,
            activityIds,
          });
        }
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att återaktivera aktiviteterna.`} ${deducedError(error)}`,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        onComplete();
      },
    });

  const validateAndSubmit = handleSubmit((formData) => {
    reactivateActivitiesMutation(formData.activityIds);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {errorFetchingActivities ? (
          <ErrorMessage
            message={t`Kunde inte hämta information om avslutade aktiviteter. Vårdperioden har registrerats, men det kan finnas aktiviteter som inte kan återaktiveras.`}
          />
        ) : closedActivities.length > 0 ? (
          <>
            <Text element="p">
              <Trans>
                Patienten har tidigare aktiviteter som avslutades vid
                utskrivning. Välj vilka aktiviteter som ska återaktiveras.
              </Trans>
            </Text>
            <ActivitiesToCloseOrReactivate
              activities={closedActivities}
              legend={t`Aktiviteter att återaktivera`}
            />
          </>
        ) : (
          <NoResults
            message={t`Det finns inga aktiviteter att återaktivera.`}
          />
        )}

        {formState.errors.root?.server?.message ? (
          <ErrorMessage message={formState.errors.root.server.message} />
        ) : null}

        <div className={styles.buttonContainer}>
          {closedActivities.length > 0 ? (
            <>
              <FilledButton type="submit" disabled={isReactivating}>
                {isReactivating ? <Spinner /> : null}
                <Trans>Återaktivera valda</Trans>
              </FilledButton>
              <OutlinedButton
                type="button"
                onClick={() => onComplete()}
                disabled={isReactivating}
              >
                <Trans>Hoppa över</Trans>
              </OutlinedButton>
            </>
          ) : (
            <FilledButton
              type="button"
              onClick={() => onComplete()}
              disabled={isReactivating}
            >
              <Trans>Fortsätt</Trans>
            </FilledButton>
          )}
        </div>
      </Form>
    </FormProvider>
  );
};
